import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Warning, Truck, Buildings, ArrowClockwise } from "@phosphor-icons/react";

const STATUS_META = {
    expired:        { label: "EXPIRED",       tone: "bg-rose-100 text-rose-700 border-rose-200",   row: "bg-rose-50" },
    expiring_soon:  { label: "EXPIRING SOON", tone: "bg-amber-100 text-amber-700 border-amber-200", row: "bg-amber-50" },
};

/**
 * FleetLicenseAlerts — Fleet Manager dashboard panel listing every vehicle
 * whose licence disk is expired or expiring within 30 days.
 */
export default function FleetLicenseAlerts() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [daysAhead, setDaysAhead] = useState(30);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/fleet/license-alerts", { params: { days_ahead: daysAhead } });
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };
    useEffect(() => { load(); }, [daysAhead]);

    return (
        <div className="space-y-4" data-testid="fleet-license-alerts">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Fleet · Licence disk alerts</div>
                    <div className="text-xl font-black text-[var(--grindrod-navy)]">Vehicles expiring in next {daysAhead} days</div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-[11px] uppercase text-slate-500 font-bold">Window</label>
                    <select value={daysAhead} onChange={(e) => setDaysAhead(Number(e.target.value))} className="g-input !py-1 !text-xs" data-testid="license-alerts-window">
                        <option value={30}>30 days</option>
                        <option value={60}>60 days</option>
                        <option value={90}>90 days</option>
                    </select>
                    <button onClick={load} className="p-1.5 rounded bg-slate-100 hover:bg-slate-200" title="Refresh"><ArrowClockwise size={14} /></button>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-slate-500 py-10">Loading…</div>
            ) : !data || data.count === 0 ? (
                <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50 p-10 text-center" data-testid="license-alerts-empty">
                    <div className="text-emerald-700 font-bold">All licence disks are valid.</div>
                    <div className="text-emerald-600 text-sm mt-1">No vehicles have a licence expiring in the next {daysAhead} days.</div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-3 gap-2" data-testid="license-alerts-summary">
                        <StatTile label="Total alerts" value={data.count} tone="text-slate-900" />
                        <StatTile label="Expired" value={data.expired} tone="text-rose-700" />
                        <StatTile label="Expiring soon" value={data.expiring_soon} tone="text-amber-700" />
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                            <Warning size={14} weight="fill" className="text-amber-600" />
                            <div className="text-[11px] uppercase tracking-widest text-slate-600 font-bold">Vehicle licence alerts</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                                    <tr>
                                        <th className="text-left px-3 py-2">Kind</th>
                                        <th className="text-left px-3 py-2">Vehicle</th>
                                        <th className="text-left px-3 py-2">Owner</th>
                                        <th className="text-left px-3 py-2">Licence expiry</th>
                                        <th className="text-right px-3 py-2">Days</th>
                                        <th className="text-right px-3 py-2">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.alerts.map((a) => {
                                        const meta = STATUS_META[a.status] || STATUS_META.expiring_soon;
                                        const Icon = a.kind === "transporter" ? Buildings : Truck;
                                        const kindLabel = a.kind === "owned_fleet" ? "Owned"
                                            : a.kind === "hired_fleet" ? "Hired"
                                            : "Contractor";
                                        return (
                                            <tr key={a.vehicle_id} className={`border-t border-slate-100 ${meta.row}`} data-testid={`license-alert-row-${a.vehicle_id}`}>
                                                <td className="px-3 py-2">
                                                    <span className="inline-flex items-center gap-1 text-slate-700 font-semibold">
                                                        <Icon size={13} weight="fill" /> {kindLabel}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 font-bold text-[var(--grindrod-navy)]">{a.label || a.plate}</td>
                                                <td className="px-3 py-2 text-slate-700">{a.owner}</td>
                                                <td className="px-3 py-2 tabular">{a.license_expiry_date || "—"}</td>
                                                <td className="px-3 py-2 text-right tabular font-bold">
                                                    {a.days_until_expiry != null ? (
                                                        a.days_until_expiry < 0 ? `−${Math.abs(a.days_until_expiry)}` : a.days_until_expiry
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-black ${meta.tone}`}>{meta.label}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                        Fleet Managers receive an in-portal Notification the first time each vehicle enters this list. Resolve them in the <b>Notifications</b> tab.
                    </div>
                </>
            )}
        </div>
    );
}

function StatTile({ label, value, tone = "text-slate-900" }) {
    return (
        <div className="rounded bg-white border border-slate-200 py-3 px-4 text-center">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
            <div className={`text-2xl font-black ${tone} tabular`}>{value ?? 0}</div>
        </div>
    );
}
